import React, { useEffect, useMemo, useState } from "react";
import {
  createBranch,
  deleteBranch,
  fetchBranches,
  fetchCompanies,
  updateBranch,
} from "../../services/organization";
import "./organization.css";

const initialForm = {
  company_id: "",
  name: "",
  address: "",
};

function normalizeList(response) {
  if (Array.isArray(response)) {
    return response;
  }

  if (Array.isArray(response?.data)) {
    return response.data;
  }

  if (Array.isArray(response?.data?.data)) {
    return response.data.data;
  }

  return [];
}

export default function BranchesPage() {
  const [items, setItems] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  async function loadData() {
    setLoading(true);
    setError("");

    try {
      const [branchResponse, companyResponse] = await Promise.all([
        fetchBranches(),
        fetchCompanies(),
      ]);

      setItems(normalizeList(branchResponse));
      setCompanies(normalizeList(companyResponse));
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load branches.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadData();
  }, []);

  const companyMap = useMemo(() => {
    return companies.reduce((acc, company) => {
      acc[String(company.id)] = company.name;
      return acc;
    }, {});
  }, [companies]);

  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) {
      return items;
    }

    return items.filter((item) =>
      [item?.name, item?.address, companyMap[String(item?.company_id)]]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(q))
    );
  }, [items, search, companyMap]);

  function resetForm() {
    setForm(initialForm);
    setEditingId(null);
  }

  function handleEdit(item) {
    setEditingId(item.id);
    setForm({
      company_id: item?.company_id || "",
      name: item?.name || "",
      address: item?.address || "",
    });
    setSuccess("");
    setError("");
  }

  async function handleDelete(id) {
    const confirmed = window.confirm("Delete this branch/campus record?");
    if (!confirmed) {
      return;
    }

    try {
      await deleteBranch(id);
      setSuccess("Branch deleted successfully.");
      if (editingId === id) {
        resetForm();
      }
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to delete branch.");
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setSaving(true);
    setError("");
    setSuccess("");

    try {
      const payload = {
        ...form,
        company_id: form.company_id || null,
      };

      if (editingId) {
        await updateBranch(editingId, payload);
        setSuccess("Branch updated successfully.");
      } else {
        await createBranch(payload);
        setSuccess("Branch created successfully.");
      }

      resetForm();
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to save branch.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="org-page">
      <section className="org-card org-page__header">
        <div>
          <span className="org-page__eyebrow">Organisation Setup</span>
          <h1>Branches / Campuses</h1>
          <p>Maintain branch and campus records linked to organization profiles.</p>
        </div>

        <div className="org-page__toolbar">
          <input
            className="org-page__search"
            type="text"
            placeholder="Search branches..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {editingId ? (
            <button
              type="button"
              className="org-page__button org-page__button--light"
              onClick={resetForm}
            >
              Cancel Edit
            </button>
          ) : null}
        </div>
      </section>

      {error ? <div className="org-error">{error}</div> : null}
      {success ? <div className="org-success">{success}</div> : null}

      <section className="org-card org-page__content">
        <form className="org-form org-form--grid" onSubmit={handleSubmit}>
          <div className="org-field">
            <label htmlFor="branch_company">Organization</label>
            <select
              id="branch_company"
              value={form.company_id}
              onChange={(e) => setForm((prev) => ({ ...prev, company_id: e.target.value }))}
              required
            >
              <option value="">Select organization</option>
              {companies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </select>
          </div>

          <div className="org-field">
            <label htmlFor="branch_name">Branch / Campus Name</label>
            <input
              id="branch_name"
              type="text"
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="Enter branch name"
              required
            />
          </div>

          <div className="org-field org-field--full">
            <label htmlFor="branch_address">Address</label>
            <textarea
              id="branch_address"
              value={form.address}
              onChange={(e) => setForm((prev) => ({ ...prev, address: e.target.value }))}
              placeholder="Enter address"
              rows={4}
            />
          </div>

          <div className="org-form__actions org-field--full">
            <button className="org-page__button" type="submit" disabled={saving}>
              {saving ? "Saving..." : editingId ? "Update Branch" : "Create Branch"}
            </button>
          </div>
        </form>
      </section>

      <section className="org-card org-page__content">
        {loading ? (
          <div className="org-loading">Loading branches...</div>
        ) : filteredItems.length === 0 ? (
          <div className="org-empty">No branch records found.</div>
        ) : (
          <div className="org-table-wrap">
            <table className="org-table">
              <thead>
                <tr>
                  <th>Branch / Campus</th>
                  <th>Organization</th>
                  <th>Address</th>
                  <th style={{ width: 160 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <div className="org-table__title">{item?.name || "-"}</div>
                    </td>
                    <td>{companyMap[String(item?.company_id)] || item?.company_id || "-"}</td>
                    <td>{item?.address || "-"}</td>
                    <td>
                      <div className="org-actions">
                        <button
                          type="button"
                          className="org-action-btn"
                          onClick={() => handleEdit(item)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="org-action-btn org-action-btn--danger"
                          onClick={() => handleDelete(item.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
